<template>
    <div
        class="rounded-xl border border-violet-200 bg-violet-100 p-4 transition-opacity duration-300 ease-in-out"
        :style="{ opacity: containerOpacity }"
    >
        <div class="mb-3 flex items-center justify-between">
            <div class="flex items-center gap-2">
                <h3 class="text-base font-bold text-violet-800">Suggestions</h3>
                <vx-badge class="gap-1!" size="xs" color="violet">
                    AI
                    <font-awesome-icon :icon="faSparkle" class="text-violet-500"></font-awesome-icon>
                </vx-badge>
            </div>
            <vx-button @click="emit('close')" size="sm" color="muted-light" class="mix-blend-multiply">
                Close
                <font-awesome-icon :icon="faXmark"></font-awesome-icon>
            </vx-button>
        </div>

        <div ref="contentContainer" class="transition-all duration-300 ease-in-out" :style="{ height: containerHeight }">
            <div
                v-if="props.loading"
                ref="loadingContainer"
                class="flex flex-col items-center justify-center pt-8 pb-14 transition-all duration-300"
                :class="props.loading ? 'opacity-100 scale-100' : 'opacity-0 scale-95'"
            >
                <font-awesome-icon :icon="faSparkle" class="text-2xl text-violet-500 sparkle-pulse mb-2" />
                <p class="text-sm font-medium text-violet-950">Generating suggestions...</p>
            </div>

            <template v-else>
                <div class="space-y-2.5 mb-4">
                    <div
                        v-for="(suggestion, index) in props.suggestions"
                        :key="index"
                        class="rounded-lg shadow-sm shadow-violet-950/10 bg-violet-50 p-3 transition-all duration-200 ease-out"
                        :class="showSuggestions ? 'opacity-100 scale-100' : 'opacity-0 scale-95'"
                        :style="{
                            transitionDelay: showSuggestions
                                ? `${index * 40}ms`
                                : `${(props.suggestions.length - 1 - index) * 40}ms`,
                        }"
                    >
                        <div class="flex items-start justify-between gap-4">
                            <p class="flex-1 text-sm leading-tight text-violet-950">
                                {{ suggestion }}
                            </p>
                            <vx-button
                                outline
                                color="muted"
                                @click="emit('select', suggestion)"
                                size="sm"
                                class="mix-blend-multiply"
                            >
                                Select
                                <font-awesome-icon :icon="faCircleCheck"></font-awesome-icon>
                            </vx-button>
                        </div>
                    </div>
                </div>

                <vx-button
                    @click="emit('generateMore')"
                    class="mix-blend-multiply w-full"
                    color="muted-light"
                    size="md"
                    :class="showButton ? 'opacity-100 scale-100' : 'opacity-0 scale-95'"
                >
                    Generate More Options
                    <font-awesome-icon :icon="faWandMagicSparkles"></font-awesome-icon>
                </vx-button>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { VxButton, VxBadge } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSparkle, faXmark, faCircleCheck, faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { ref, watch, nextTick } from 'vue';

const props = defineProps<{
    loading: boolean;
    suggestions: string[];
}>();

const emit = defineEmits<{
    select: [text: string];
    'generateMore': [];
    close: [];
}>();

const containerOpacity = ref(1);
const containerHeight = ref<string>('auto');
const showSuggestions = ref(false);
const showButton = ref(false);
const contentContainer = ref<HTMLElement | null>(null);
const loadingContainer = ref<HTMLElement | null>(null);

watch(
    () => props.loading,
    async (loading, wasLoading) => {
        if (loading) {
            showSuggestions.value = false;
            showButton.value = false;

            if (contentContainer.value) {
                const currentHeight = contentContainer.value.offsetHeight;
                containerHeight.value = `${currentHeight}px`;
            }

            await nextTick();

            if (loadingContainer.value) {
                const loadingHeight = loadingContainer.value.offsetHeight;
                containerHeight.value = `${loadingHeight}px`;
            }
        } else if (wasLoading) {
            if (contentContainer.value) {
                const currentHeight = contentContainer.value.offsetHeight;
                containerHeight.value = `${currentHeight}px`;
            }

            await nextTick();

            if (contentContainer.value) {
                const newHeight = contentContainer.value.scrollHeight;
                containerHeight.value = `${newHeight}px`;
            }

            await new Promise((resolve) => setTimeout(resolve, 50));
            showSuggestions.value = true;

            await new Promise((resolve) => setTimeout(resolve, 200));
            showButton.value = true;

            await new Promise((resolve) => setTimeout(resolve, 300));
            containerHeight.value = 'auto';
        }
    },
    { immediate: true }
);

watch(
    () => props.suggestions,
    async () => {
        if (!props.loading && props.suggestions.length > 0) {
            showSuggestions.value = true;
            showButton.value = true;
        }
    },
    { immediate: true }
);
</script>

<style scoped>
@keyframes sparkle-pulse {
    0%,
    100% {
        transform: scale(0.9);
        opacity: 0.6;
    }
    50% {
        transform: scale(1);
        opacity: 1;
    }
}

.sparkle-pulse {
    animation: sparkle-pulse 1.2s cubic-bezier(0.5, 0, 0.1, 1) infinite;
}
</style>
